import React, { useState } from 'react';

/**
 * SourcingTab - Phase 3 content
 * Renders product sourcing data, supplier directory, and outreach plans
 */
const fmtCAD = (v) => {
  const n = parseFloat(v);
  if (!n || isNaN(n)) return null;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
};

const SourcingTab = ({ tender, phase1Data, phase2Data, phase2bData, phase2cData, phase3Data }) => {
  const [copiedEmail, setCopiedEmail] = useState(null);

  // Build validated pricing lookup from Phase 2
  const pricingComparisons = phase2Data?.pricing_comparisons || [];
  const pricingMap = {};
  pricingComparisons.forEach(pc => {
    pricingMap[(pc.product_name || '').toLowerCase()] = pc;
  });

  // Fuzzy pricing lookup — Phase 2 names may include delivery suffixes that Phase 3 omits
  const findPricing = (productName) => {
    const key = (productName || '').toLowerCase();
    // Exact match first
    if (pricingMap[key]) return pricingMap[key];
    // Substring match: find Phase 2 entries whose name starts with or contains this product name
    return pricingComparisons.find(pc => {
      const pcName = (pc.product_name || '').toLowerCase();
      return pcName.startsWith(key) || key.startsWith(pcName);
    }) || null;
  };

  const handleCopyEmail = (email, idx) => {
    navigator.clipboard.writeText(email).then(() => {
      setCopiedEmail(idx);
      setTimeout(() => setCopiedEmail(null), 2000);
    });
  };

  // No phase3 data yet
  if (!phase3Data) {
    return (
      <div className="sourcing-tab">
        <div className="placeholder-content">
          <div className="placeholder-icon" style={{ fontSize: '32px', color: '#9ca3af' }}>&#9881;</div>
          <div className="placeholder-title">Supplier Shortlist</div>
          <div className="placeholder-description">
            Run the AI analysis pipeline from your Saved Tenders to generate
            supplier recommendations, contact info, and draft inquiry emails.
          </div>
        </div>
      </div>
    );
  }

  const productSourcing = phase3Data.product_sourcing || [];
  const massOutreach = phase3Data.mass_outreach_plan;
  const uniqueSuppliers = phase3Data.unique_suppliers_count || 0;

  // Build Phase 2c strategy lookup
  const strategyMap = {};
  (phase2cData?.product_strategies || []).forEach(ps => {
    strategyMap[(ps.product_name || '').toLowerCase()] = ps;
  });

  // Cross-reference Phase 1 products for descriptions/specs
  const phase1Products = phase1Data?.products || [];
  const getProductDetails = (productName) => {
    if (!productName) return null;
    const name = productName.toLowerCase();
    return phase1Products.find(p =>
      p.product_name && name.includes(p.product_name.toLowerCase())
        || p.product_name && p.product_name.toLowerCase().includes(name.split(' - ')[0].toLowerCase())
    );
  };

  // Delivery location from tender metadata
  const deliveryRegions = tender?.['deliveryRegions-régionsLivraison']
    || tender?.business_intelligence?.delivery_regions
    || [];
  const deliveryText = Array.isArray(deliveryRegions) && deliveryRegions.length > 0
    ? deliveryRegions.join(', ')
    : typeof deliveryRegions === 'string' && deliveryRegions
      ? deliveryRegions
      : null;

  return (
    <div className="sourcing-tab">
      {/* Stats */}
      <div className="sourcing-stats">
        <div className="sourcing-stat">
          <span className="stat-value">{productSourcing.length}</span>
          <span className="stat-label">Products</span>
        </div>
        <div className="sourcing-stat">
          <span className="stat-value">{uniqueSuppliers}</span>
          <span className="stat-label">Unique Suppliers</span>
        </div>
      </div>

      {/* Product Sourcing Cards */}
      {productSourcing.map((product, pIdx) => {
        const p1 = getProductDetails(product.product_name);
        const specs = p1?.description || product.description || null;
        const qty = p1?.quantity ? `${p1.quantity}${p1.unit ? ` ${p1.unit}` : ''}` : null;

        // Quick lookups for summary line
        const pcQuick = findPricing(product.product_name);
        const stQuick = strategyMap[(product.product_name || '').toLowerCase()];

        return (
        <div key={pIdx} className="sourcing-product-card">
          <details open={pIdx === 0}>
          <summary style={{ cursor: 'pointer', listStyle: 'none', WebkitAppearance: 'none' }}>
          <h3 className="summary-section-title" style={{ marginBottom: '0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '10px', color: '#6b7280', transition: 'transform 0.15s' }} className="details-chevron">&#9654;</span>
            {product.product_name || `Product ${pIdx + 1}`}
            {qty && <span style={{ fontSize: '11px', fontWeight: 400, color: '#6b7280' }}>Qty: {qty}</span>}
            {pcQuick?.best_cost_cad && (
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#16a34a' }}>
                Best: {fmtCAD(pcQuick.best_cost_cad)}
              </span>
            )}
            {stQuick?.recommendation && (
              <span style={{
                fontSize: '9px', padding: '1px 6px', borderRadius: '4px', fontWeight: 600,
                background: stQuick.recommendation === 'OVERSEAS' ? '#fce7f3' : stQuick.recommendation === 'DOMESTIC' ? '#dbeafe' : '#f3f4f6',
                color: stQuick.recommendation === 'OVERSEAS' ? '#be185d' : stQuick.recommendation === 'DOMESTIC' ? '#1d4ed8' : '#6b7280',
              }}>
                {stQuick.recommendation}
              </span>
            )}
            {(product.suppliers || []).length + ((stQuick?.overseas?.alibaba_listings || product.alibaba_listings || []).length) > 0 && (
              <span style={{ fontSize: '10px', fontWeight: 400, color: '#9ca3af' }}>
                {(product.suppliers || []).length + ((stQuick?.overseas?.alibaba_listings || product.alibaba_listings || []).length)} suppliers
              </span>
            )}
          </h3>
          </summary>

          {/* Product description: specs, delivery, quantity */}
          {(specs || deliveryText || qty) && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '12px',
              fontSize: '12px',
              color: '#6b7280',
              lineHeight: '1.4'
            }}>
              {specs && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: '#f3f4f6',
                  padding: '3px 8px',
                  borderRadius: '4px'
                }}>
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>Specs:</span> {specs}
                </span>
              )}
              {deliveryText && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: '#eff6ff',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  color: '#1e40af'
                }}>
                  <span style={{ fontSize: '11px', color: '#1e40af' }}>Delivery:</span> {deliveryText}
                </span>
              )}
              {qty && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: '#f0fdf4',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  color: '#166534'
                }}>
                  <span style={{ fontSize: '11px', color: '#166534' }}>Qty:</span> {qty}
                </span>
              )}
            </div>
          )}

          {/* Pricing Insight from validated Phase 2 data */}
          {(() => {
            const pc = findPricing(product.product_name);
            if (!pc) return null;
            const hasSources = pc.price_sources && pc.price_sources.length > 0;
            const routeStyles = {
              domestic: { background: '#dbeafe', color: '#1d4ed8', label: 'Domestic' },
              factory: { background: '#fce7f3', color: '#be185d', label: 'Factory' },
              unknown: { background: '#f3f4f6', color: '#6b7280', label: 'Unknown' },
            };
            const badge = routeStyles[pc.sourcing_route] || routeStyles.unknown;
            return (
              <div style={{
                background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
                padding: '10px 14px', marginBottom: '12px', fontSize: '12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Pricing Insight</span>
                  <span style={{
                    padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                    background: badge.background, color: badge.color,
                  }}>
                    {badge.label}
                  </span>
                  {pc.validation_status === 'validated' && (
                    <span style={{ padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, background: '#dcfce7', color: '#16a34a' }}>
                      Verified
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: hasSources ? '8px' : 0 }}>
                  {pc.best_cost_cad && (
                    <span><strong>Best Cost:</strong> {fmtCAD(pc.best_cost_cad)}</span>
                  )}
                  {pc.domestic_wholesale_cad && (
                    <span><strong>Domestic:</strong> {fmtCAD(pc.domestic_wholesale_cad)}</span>
                  )}
                  {pc.factory_landed_cad && (
                    <span><strong>Factory Landed:</strong> {fmtCAD(pc.factory_landed_cad)}</span>
                  )}
                  {pc.historical_price_paid && (
                    <span><strong>Gov't Paid:</strong> {fmtCAD(pc.historical_price_paid)}</span>
                  )}
                  {pc.margin_on_best_cost != null && (
                    <span style={{ color: '#059669', fontWeight: 600 }}>{pc.margin_on_best_cost}% margin</span>
                  )}
                </div>
                {pc.sourcing_note && (
                  <div style={{ color: '#1e40af', fontSize: '11px', marginBottom: hasSources ? '6px' : 0 }}>
                    {pc.sourcing_note}
                  </div>
                )}
                {hasSources && (
                  <details style={{ marginTop: '4px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>
                      Data Sources ({pc.price_sources.length})
                    </summary>
                    <ul style={{ margin: '4px 0 0', paddingLeft: '16px', color: '#374151', fontSize: '11px' }}>
                      {pc.price_sources.map((src, sIdx) => (
                        <li key={sIdx} style={{ marginBottom: '1px' }}>{src}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })()}

          {/* Phase 2c Sourcing Strategy */}
          {(() => {
            const sKey = (product.product_name || '').toLowerCase();
            const strategy = strategyMap[sKey];
            if (!strategy) return null;
            const recStyles = {
              DOMESTIC: { bg: '#dbeafe', color: '#1d4ed8' },
              OVERSEAS: { bg: '#fce7f3', color: '#be185d' },
              EITHER: { bg: '#f3f4f6', color: '#6b7280' },
            };
            const recStyle = recStyles[strategy.recommendation] || recStyles.EITHER;
            return (
              <details style={{ marginBottom: '12px' }}>
                <summary style={{
                  cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#374151',
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0',
                }}>
                  Sourcing Strategy
                  <span style={{
                    padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                    background: recStyle.bg, color: recStyle.color,
                  }}>
                    {strategy.recommendation}
                  </span>
                  {strategy.savings_pct != null && (
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 400 }}>
                      {strategy.savings_pct > 0 ? `${strategy.savings_pct}% savings` : `${Math.abs(strategy.savings_pct)}% more expensive`}
                    </span>
                  )}
                </summary>
                <div style={{
                  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
                  padding: '12px', fontSize: '12px', marginTop: '4px',
                }}>
                  {strategy.rationale && (
                    <p style={{ margin: '0 0 10px', color: '#374151', lineHeight: '1.5' }}>
                      {strategy.rationale}
                    </p>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {/* Domestic option */}
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px' }}>
                      <div style={{ fontWeight: 600, fontSize: '11px', color: '#1d4ed8', marginBottom: '6px' }}>
                        Domestic
                      </div>
                      {strategy.domestic?.supplier_name && (
                        <div style={{ fontSize: '11px', marginBottom: '4px' }}>
                          <strong>{strategy.domestic.supplier_name}</strong>
                        </div>
                      )}
                      {strategy.domestic?.unit_price_cad && (
                        <div style={{ fontSize: '11px' }}>Unit: {fmtCAD(strategy.domestic.unit_price_cad)}</div>
                      )}
                      {strategy.domestic?.lead_time_days && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Lead: {strategy.domestic.lead_time_days} days</div>
                      )}
                      {strategy.domestic?.pros?.length > 0 && (
                        <ul style={{ margin: '4px 0 0', paddingLeft: '14px', fontSize: '10px', color: '#16a34a' }}>
                          {strategy.domestic.pros.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                      )}
                      {strategy.domestic?.cons?.length > 0 && (
                        <ul style={{ margin: '2px 0 0', paddingLeft: '14px', fontSize: '10px', color: '#dc2626' }}>
                          {strategy.domestic.cons.map((c, i) => <li key={i}>{c}</li>)}
                        </ul>
                      )}
                    </div>
                    {/* Overseas option */}
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px' }}>
                      <div style={{ fontWeight: 600, fontSize: '11px', color: '#be185d', marginBottom: '6px' }}>
                        Overseas
                      </div>
                      {strategy.overseas?.factory_name && (
                        <div style={{ fontSize: '11px', marginBottom: '4px' }}>
                          <strong>{strategy.overseas.factory_name}</strong>
                          {strategy.overseas.country && ` (${strategy.overseas.country})`}
                        </div>
                      )}
                      {strategy.overseas?.landed_cost_cad && (
                        <div style={{ fontSize: '11px' }}>Landed: {fmtCAD(strategy.overseas.landed_cost_cad)}</div>
                      )}
                      {strategy.overseas?.lead_time_weeks && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Lead: {strategy.overseas.lead_time_weeks} weeks</div>
                      )}
                      {strategy.overseas?.risk_score && (
                        <div style={{ fontSize: '10px', color: strategy.overseas.risk_score === 'high' ? '#dc2626' : strategy.overseas.risk_score === 'medium' ? '#d97706' : '#16a34a' }}>
                          Risk: {strategy.overseas.risk_score}
                        </div>
                      )}
                      {strategy.overseas?.pros?.length > 0 && (
                        <ul style={{ margin: '4px 0 0', paddingLeft: '14px', fontSize: '10px', color: '#16a34a' }}>
                          {strategy.overseas.pros.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                      )}
                      {strategy.overseas?.cons?.length > 0 && (
                        <ul style={{ margin: '2px 0 0', paddingLeft: '14px', fontSize: '10px', color: '#dc2626' }}>
                          {strategy.overseas.cons.map((c, i) => <li key={i}>{c}</li>)}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </details>
            );
          })()}

          {/* Unified Suppliers & Pricing Table — merges Phase 3 suppliers + Alibaba API + Google Shopping */}
          {(() => {
            const sKey = (product.product_name || '').toLowerCase();
            const strategy = strategyMap[sKey];
            const alibabaListings = strategy?.overseas?.alibaba_listings || product.alibaba_listings || [];
            const shopListings = (phase2bData?.shopping_listings || {})[product.product_name] || [];
            const phase3Suppliers = product.suppliers || [];

            // Build unified rows with numeric price for sorting
            const buildRow = (name, location, type, website, priceStr, priceNum, priceNote, moq, email, phone, source, verified, rating, category) => ({
              name, location, type, website, price: priceStr, priceNum, priceNote, moq: moq || '', email: email || '', phone: phone || '', source, verified: verified || false, rating, category,
            });

            const allRows = [];

            // Phase 3 Claude suppliers
            phase3Suppliers.forEach(s => {
              const pNum = typeof s.listed_price === 'number' ? s.listed_price : (parseFloat(s.listed_price) || null);
              const isOverseas = ['manufacturer', 'foreign'].includes(s.type);
              allRows.push(buildRow(
                s.company_name || 'Unknown', s.location || '', s.type || 'distributor',
                s.website || s.product_url || '',
                pNum ? fmtCAD(pNum) : null, pNum,
                s.currency && s.currency !== 'CAD' ? s.currency : '', null,
                s.email || '', s.phone || '', 'ai_analysis', s.verified, s.rating,
                isOverseas ? 'overseas' : 'domestic',
              ));
            });

            // Alibaba API listings → overseas
            alibabaListings.forEach(l => {
              const pMin = l.price_min_cad;
              const priceStr = pMin != null
                ? (l.price_max_cad && l.price_max_cad !== pMin
                  ? `$${pMin.toFixed(2)}-$${l.price_max_cad.toFixed(2)} CAD`
                  : `$${pMin.toFixed(2)} CAD`)
                : null;
              const usdStr = l.price_min_usd != null
                ? (l.price_max_usd && l.price_max_usd !== l.price_min_usd
                  ? `$${l.price_min_usd.toFixed(2)}-$${l.price_max_usd.toFixed(2)} USD`
                  : `$${l.price_min_usd.toFixed(2)} USD`)
                : '';
              allRows.push(buildRow(
                l.supplier_name || 'Unknown Supplier',
                l.years_on_alibaba ? `${l.years_on_alibaba} years on Alibaba` : 'China',
                'wholesale', l.product_url || '', priceStr, pMin, usdStr,
                l.moq ? `MOQ: ${l.moq} ${l.moq_unit || ''}` : '',
                '', '', 'alibaba', l.verified, null, 'overseas',
              ));
            });

            // Google Shopping listings → domestic
            shopListings.forEach(l => {
              allRows.push(buildRow(
                l.merchant || 'Unknown Merchant', 'Canada', 'retail',
                l.product_url || '',
                l.price_cad != null ? `$${l.price_cad.toFixed(2)} CAD` : null, l.price_cad,
                (l.product_name || '').substring(0, 50), null,
                '', '', 'google_shopping', false, l.rating, 'domestic',
              ));
            });

            if (allRows.length === 0) {
              return <p style={{ fontSize: '12px', color: '#6b7280' }}>No suppliers found for this product.</p>;
            }

            // Sort within categories by price (cheapest first, nulls last)
            const sortByPrice = (a, b) => {
              if (a.priceNum == null && b.priceNum == null) return 0;
              if (a.priceNum == null) return 1;
              if (b.priceNum == null) return -1;
              return a.priceNum - b.priceNum;
            };

            const overseasRows = allRows.filter(r => r.category === 'overseas').sort(sortByPrice);
            const domesticRows = allRows.filter(r => r.category === 'domestic').sort(sortByPrice);

            // Cheapest prices per category
            const cheapestOverseas = overseasRows.find(r => r.priceNum != null);
            const cheapestDomestic = domesticRows.find(r => r.priceNum != null);

            const sourceStyles = {
              alibaba: { label: 'Alibaba', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
              google_shopping: { label: 'Google Shopping', bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
              ai_analysis: { label: 'AI Sourced', bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
            };
            const typeStyles = {
              oem: { bg: '#dbeafe', color: '#1d4ed8' },
              wholesale: { bg: '#fff7ed', color: '#c2410c' },
              retail: { bg: '#dbeafe', color: '#1d4ed8' },
              manufacturer: { bg: '#fce7f3', color: '#be185d' },
              foreign: { bg: '#fce7f3', color: '#be185d' },
              domestic_distributor: { bg: '#f3f4f6', color: '#374151' },
              distributor: { bg: '#f3f4f6', color: '#374151' },
            };

            const renderRow = (row, rIdx, isCheapest) => {
              const srcStyle = sourceStyles[row.source] || sourceStyles.ai_analysis;
              const tStyle = typeStyles[row.type] || typeStyles.distributor;
              return (
                <tr key={rIdx} style={isCheapest ? { background: '#f0fdf4' } : undefined}>
                  <td>
                    <strong>{row.name}</strong>
                    {row.location && (
                      <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>{row.location}</div>
                    )}
                    <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                      {row.verified && (
                        <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', fontWeight: 600, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>Verified</span>
                      )}
                      {row.rating != null && (
                        <span style={{ fontSize: '10px', color: '#d97706' }}>{'★'.repeat(Math.round(row.rating))} {row.rating}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600, background: srcStyle.bg, color: srcStyle.color, border: `1px solid ${srcStyle.border}`, whiteSpace: 'nowrap' }}>
                      {srcStyle.label}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: tStyle.bg, color: tStyle.color }}>
                      {row.type}
                    </span>
                  </td>
                  <td style={{ fontWeight: 500 }}>
                    {row.price || '—'}
                    {isCheapest && <span style={{ fontSize: '9px', marginLeft: '4px', color: '#16a34a', fontWeight: 700 }}>BEST</span>}
                    {row.priceNote && <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '1px' }}>{row.priceNote}</div>}
                    {row.moq && <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '1px' }}>{row.moq}</div>}
                  </td>
                  <td>
                    {row.website ? (
                      <a href={row.website.startsWith('http') ? row.website : `https://${row.website}`}
                        target="_blank" rel="noopener noreferrer" className="contract-link" onClick={(e) => e.stopPropagation()}>Visit</a>
                    ) : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px' }}>
                      {row.email && row.email !== 'verify' && <span>{row.email}</span>}
                      {row.phone && row.phone !== 'verify' && <span>{row.phone}</span>}
                      {(!row.email || row.email === 'verify') && (!row.phone || row.phone === 'verify') && (
                        <span style={{ color: '#9ca3af', fontSize: '10px' }}>—</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            };

            const categoryHeader = (label, icon, count, cheapest, color) => (
              <tr key={`hdr-${label}`}>
                <td colSpan={6} style={{
                  background: '#f9fafb', padding: '8px 12px', fontWeight: 700, fontSize: '12px',
                  borderBottom: '2px solid #e5e7eb', color: '#374151',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{icon} {label} <span style={{ fontWeight: 400, color: '#6b7280' }}>({count})</span></span>
                    {cheapest && (
                      <span style={{ fontSize: '11px', color, fontWeight: 600 }}>
                        Best price: {cheapest.price}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );

            return (
              <div className="contracts-list">
                <table className="contracts-table" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Source</th>
                      <th>Type</th>
                      <th>Price</th>
                      <th>Website</th>
                      <th>Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overseasRows.length > 0 && (
                      <>
                        {categoryHeader('Overseas / Wholesale', '', overseasRows.length, cheapestOverseas, '#be185d')}
                        {overseasRows.map((row, i) => renderRow(row, `o-${i}`, cheapestOverseas && row === cheapestOverseas))}
                      </>
                    )}
                    {domesticRows.length > 0 && (
                      <>
                        {categoryHeader('Domestic / Canadian', '', domesticRows.length, cheapestDomestic, '#1d4ed8')}
                        {domesticRows.map((row, i) => renderRow(row, `d-${i}`, cheapestDomestic && row === cheapestDomestic))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Draft inquiry email */}
          {product.draft_inquiry_email && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Draft Inquiry Email
                </span>
                <button
                  className="tab-action-btn"
                  onClick={() => handleCopyEmail(product.draft_inquiry_email, pIdx)}
                  style={{ fontSize: '11px', padding: '4px 10px' }}
                >
                  {copiedEmail === pIdx ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '12px',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                color: '#374151',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {product.draft_inquiry_email}
              </pre>
            </div>
          )}
        </details>
        </div>
        );
      })}

      {/* Mass Outreach Plan */}
      {massOutreach && (
        <div className="summary-section" style={{ marginTop: '24px' }}>
          <h3 className="summary-section-title">
            <span className="icon" style={{ fontSize: '14px' }}>&#9993;</span>
            Mass Outreach Plan
          </h3>
          <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
            {typeof massOutreach === 'string' ? (
              <p>{massOutreach}</p>
            ) : (
              <pre style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                whiteSpace: 'pre-wrap',
                fontSize: '12px'
              }}>
                {JSON.stringify(massOutreach, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Empty products */}
      {productSourcing.length === 0 && (
        <div className="placeholder-content">
          <div className="placeholder-icon" style={{ fontSize: '32px', color: '#9ca3af' }}>&#9881;</div>
          <div className="placeholder-title">No Sourcing Data</div>
          <div className="placeholder-description">
            The sourcing analysis did not return product data. This may happen if the tender
            does not have specific product requirements.
          </div>
        </div>
      )}
    </div>
  );
};

export default SourcingTab;
